/**
 * Voice Call Modal — watching a phone call happen.
 *
 * Opens the moment the analyst approves a call and stays open until the
 * call is finished with. It reads the `voice_calls` row rather than the
 * chat stream, because the stream can't tell it anything: the copilot
 * tool that placed the call is blocked for the call's duration, and no
 * SSE event escapes a tool node until it returns.
 *
 * Two acts, because that is what the data supports
 * ------------------------------------------------
 * ElevenLabs returns an empty transcript for the whole call and fills it
 * in one go when the call ends (measured — see docs/features/voice-calls).
 * So there is no live transcript to show, and an empty transcript panel
 * sitting there mid-call reads as a broken feature rather than an absent
 * one.
 *
 * Act one, while the call runs: what we genuinely know live — that it is
 * ringing, that someone answered, how long it has been going, and what we
 * asked for. Act two, when it ends: the answer, then the transcript.
 *
 * If a real-time feed becomes available later (the enterprise monitoring
 * socket, or owning the audio path), it writes to the same row and this
 * file grows a transcript panel in act one. Nothing else changes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography, CircularProgress, keyframes } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { AppDialog } from '../../../components/dialog';
import { fetchCall, fetchLatestCallForThread } from '../../../services/voiceCallService';
import type { VoiceCall } from '../../../types/voiceCall';

const POLL_INTERVAL_MS = 1000;

/** How long to wait for the call row to appear before admitting something
 *  went wrong. The tool writes it within a second of the approval; ten
 *  seconds of nothing means it never got that far. */
const ROW_APPEARS_WITHIN_MS = 10_000;

const pulse = keyframes`
  0%   { opacity: 1;   transform: scale(1); }
  50%  { opacity: 0.35; transform: scale(0.86); }
  100% { opacity: 1;   transform: scale(1); }
`;

type Phase = 'connecting' | 'dialing' | 'live' | 'wrapping' | 'done' | 'lost';

function phaseOf(call: VoiceCall | null, waitedTooLong: boolean): Phase {
  if (!call) return waitedTooLong ? 'lost' : 'connecting';
  if (call.isFinal) return 'done';
  if (call.status === 'queued' || call.status === 'ringing') return 'dialing';
  if (call.status === 'in_progress') return 'live';
  // Audio over, extraction still running.
  return 'wrapping';
}

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** `premium_amount` → `Premium amount`. The field names come from the voice
 *  agent's own configuration, so they arrive as the operator typed them. */
function humaniseKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

/**
 * Is this row the call we opened for, or the one before it?
 *
 * `/latest` is thread-scoped, and at the moment the modal opens our row
 * does not exist yet — the tool writes it a beat after the approval
 * resumes. So the first poll usually returns the *previous* call on the
 * thread. Rendering that means showing a finished call's transcript and,
 * because it is already final, never polling again.
 *
 * Two signals settle it without trusting the browser's clock:
 *   - a row that isn't finished yet must be ours; the old one is done
 *   - once a row appears with an id different from the one that was there
 *     when we opened, that's ours, however fast it finished
 */
function isOurs(
  candidate: VoiceCall,
  staleId: React.MutableRefObject<string | null>,
  ourId: React.MutableRefObject<string | null>,
): boolean {
  if (ourId.current) return candidate.callId === ourId.current;

  // A call still in flight must be ours; the previous one is finished.
  if (!candidate.isFinal) {
    ourId.current = candidate.callId;
    return true;
  }

  // A finished call on the first poll is the previous one. Remember its id.
  if (staleId.current === null) {
    staleId.current = candidate.callId;
    return false;
  }

  // Still that same finished call.
  if (staleId.current === candidate.callId) return false;

  // A different finished call — ours, and it finished before we ever saw it
  // live, which is what a call that failed to place looks like.
  ourId.current = candidate.callId;
  return true;
}

type StageState = 'pending' | 'active' | 'done';

/** The three things we genuinely know about a call in flight. */
function stagesFor(phase: Phase): Array<{ label: string; state: StageState }> {
  const order: Phase[] = ['connecting', 'dialing', 'live', 'wrapping', 'done'];
  const at = Math.max(0, order.indexOf(phase));
  const stateAt = (index: number): StageState =>
    at > index ? 'done' : at === index ? 'active' : 'pending';
  return [
    // 'connecting' and 'dialing' are both "the phone is ringing" as far as
    // anyone watching is concerned — the first is just us not having seen
    // the row yet.
    { label: 'Ringing', state: at <= 1 ? 'active' : 'done' },
    { label: 'On the call', state: stateAt(2) },
    { label: 'Writing up what was said', state: stateAt(3) },
  ];
}

interface Props {
  open: boolean;
  threadId: string | null;
  /** A specific finished call, opened from its card in the chat. When null
   *  the modal follows the thread's newest call — the live case, where the
   *  call id does not exist yet at the moment the modal opens. */
  callId?: string | null;
  onClose: () => void;
}

export default function VoiceCallModal({ open, threadId, callId, onClose }: Props) {
  const theme = useTheme();
  const [call, setCall] = useState<VoiceCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const openedAt = useRef<number>(Date.now());
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  /** Whether we've ever seen the row. Deliberately a ref, not state: if
   *  `poll` closed over `call`, every response would give it a new identity,
   *  which would tear down and rebuild the interval and fire another request
   *  immediately — a poll loop running as fast as the network allows. */
  const seenCallRef = useRef(false);
  /** The thread's newest call at the moment we opened — i.e. the *previous*
   *  call, if there was one. */
  const staleCallIdRef = useRef<string | null>(null);
  /** Once we've decided which call is ours, we follow only that one. */
  const ourCallIdRef = useRef<string | null>(null);

  const phase = phaseOf(call, waitedTooLong);
  const isSettled = phase === 'done' || phase === 'lost';

  // ── Polling ────────────────────────────────────────────────────────────
  const poll = useCallback(async () => {
    // Opened from a card: we know exactly which call, so there is no
    // ambiguity to resolve and nothing to wait for.
    if (callId) {
      try {
        const next = await fetchCall(callId);
        seenCallRef.current = true;
        setCall(next);
        setError(null);
      } catch (e: any) {
        if (!seenCallRef.current) setError(e?.message || 'Could not load the call');
      }
      return;
    }

    if (!threadId) return;
    try {
      const next = await fetchLatestCallForThread(threadId);

      if (next && !isOurs(next, staleCallIdRef, ourCallIdRef)) {
        // The previous call on this thread, still the newest row because
        // ours hasn't been written yet. Keep waiting rather than showing it.
        if (Date.now() - openedAt.current > ROW_APPEARS_WITHIN_MS) {
          setWaitedTooLong(true);
        }
        return;
      }

      if (next) {
        seenCallRef.current = true;
        setCall(next);
        setError(null);
      } else if (Date.now() - openedAt.current > ROW_APPEARS_WITHIN_MS) {
        setWaitedTooLong(true);
      }
    } catch (e: any) {
      // A dropped poll during a live call is noise — the next one usually
      // succeeds. Only surface it once we have nothing at all to show.
      if (!seenCallRef.current) setError(e?.message || 'Could not reach the call');
    }
  }, [threadId, callId]);

  useEffect(() => {
    if (!open) return;
    openedAt.current = Date.now();
    seenCallRef.current = false;
    staleCallIdRef.current = null;
    ourCallIdRef.current = null;
    setWaitedTooLong(false);
    setCall(null);
    setError(null);
  }, [open, callId]);

  useEffect(() => {
    if (!open || isSettled) return undefined;
    poll();
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [open, isSettled, poll]);

  // Local clock so the timer ticks between polls rather than jumping a
  // second at a time.
  useEffect(() => {
    if (!open || isSettled) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [open, isSettled]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [call?.transcript.length]);

  // ── Derived display ────────────────────────────────────────────────────
  const elapsed = useMemo(() => {
    if (!call) return 0;
    // The provider reports `call_duration_secs: 0` for a call in flight, so
    // trusting it while live pins the timer at 00:00. Wall clock until the
    // call is over; the provider's own figure once it can be believed.
    if (call.isFinal && call.durationSecs != null) return call.durationSecs;
    return (now - new Date(call.createdAt).getTime()) / 1000;
  }, [call, now]);

  const extractedEntries = useMemo(
    () => Object.entries(call?.extracted || {}),
    [call?.extracted],
  );

  const statusChip = {
    connecting: { label: 'Connecting', color: theme.tokens.text.secondary },
    dialing: { label: 'Ringing', color: theme.tokens.semantic.info },
    live: { label: 'On the call', color: theme.tokens.semantic.success },
    wrapping: { label: 'Call ended', color: theme.tokens.semantic.info },
    done: { label: 'Call ended', color: theme.tokens.text.secondary },
    lost: { label: 'Not started', color: theme.tokens.semantic.danger },
  }[phase];

  const failed = call && (call.status === 'failed' || call.status === 'timeout');

  const subtitle = call?.objective
    ? call.objective.charAt(0).toUpperCase() + call.objective.slice(1)
    : 'Placing the call…';

  const sectionLabelSx = {
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.tokens.text.tertiary,
  };

  const chips = call && (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={call.toNumber}
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.72rem',
          bgcolor: theme.tokens.surface.sunken,
        }}
      />
      {call.policyNumber && (
        <Chip size="small" variant="outlined" label={call.policyNumber} sx={{ fontSize: '0.72rem' }} />
      )}
    </Stack>
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      icon={<PhoneInTalkRoundedIcon fontSize="small" />}
      title={call?.carrierName || 'Calling…'}
      subtitle={subtitle}
      headerExtras={
        <Stack direction="row" spacing={1} alignItems="center">
          {!isSettled && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusChip.color,
                animation: `${pulse} 1.4s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{ color: statusChip.color, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {statusChip.label}
          </Typography>
          {call && (
            <Typography
              variant="caption"
              sx={{
                color: theme.tokens.text.tertiary,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 40,
              }}
            >
              {formatDuration(elapsed)}
            </Typography>
          )}
        </Stack>
      }
      // A single button. The default "Cancel" would imply it hangs up the
      // call, which nothing here can do.
      actions={
        <Button variant="contained" onClick={onClose}>
          {isSettled ? 'Close' : 'Hide'}
        </Button>
      }
    >
      <Stack spacing={2.5}>
        {chips}

        {failed ? (
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              p: 2,
              borderRadius: `${theme.ds.radius.md}px`,
              bgcolor: theme.tokens.semantic.dangerSoft,
              border: `1px solid ${theme.tokens.semantic.danger}`,
            }}
          >
            <ErrorOutlineRoundedIcon
              fontSize="small"
              sx={{ color: theme.tokens.semantic.danger, mt: '2px' }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {call?.status === 'timeout'
                  ? 'We stopped following this call'
                  : 'The call could not be placed'}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.tokens.text.secondary, mt: 0.5 }}>
                {call?.error || 'No further detail was given.'}
              </Typography>
            </Box>
          </Stack>
        ) : phase === 'lost' ? (
          <Typography variant="body2" sx={{ color: theme.tokens.text.secondary, py: 3 }}>
            {error || 'The call never started. Nothing was dialed.'}
          </Typography>
        ) : isSettled ? (
          /* ── Act two: the reveal ──────────────────────────────────── */
          <>
            <Box>
              <Typography sx={sectionLabelSx}>What the call established</Typography>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: `${theme.ds.radius.md}px`,
                  border: `1px solid ${theme.tokens.border.subtle}`,
                  bgcolor: theme.tokens.surface.sunken,
                  px: 2,
                  py: extractedEntries.length ? 1 : 1.5,
                }}
              >
                {extractedEntries.length > 0 ? (
                  <Stack
                    divider={<Box sx={{ height: '1px', bgcolor: theme.tokens.border.subtle }} />}
                  >
                    {extractedEntries.map(([key, value]) => (
                      <Stack
                        key={key}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        spacing={2}
                        sx={{ py: 1 }}
                      >
                        <Typography variant="body2" sx={{ color: theme.tokens.text.secondary }}>
                          {humaniseKey(key)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                          }}
                        >
                          {formatValue(value)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.tokens.text.tertiary }}>
                    Nothing was established on this call.
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography sx={sectionLabelSx}>Transcript</Typography>
              <Box
                sx={{
                  mt: 1,
                  maxHeight: 260,
                  overflowY: 'auto',
                  borderRadius: `${theme.ds.radius.md}px`,
                  border: `1px solid ${theme.tokens.border.subtle}`,
                  bgcolor: theme.tokens.surface.raised,
                  px: 2,
                  py: 1.5,
                }}
              >
                {call && call.transcript.length > 0 ? (
                  <Stack spacing={1.5}>
                    {call.transcript.map((turn, i) => (
                      <Stack
                        key={`${turn.seconds}-${i}`}
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            mt: '2px',
                            color:
                              turn.role === 'agent'
                                ? theme.tokens.text.accent
                                : theme.tokens.text.tertiary,
                            display: 'flex',
                          }}
                        >
                          {turn.role === 'agent' ? (
                            <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <PersonOutlineRoundedIcon sx={{ fontSize: 16 }} />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              turn.role === 'agent'
                                ? theme.tokens.text.primary
                                : theme.tokens.text.secondary,
                            lineHeight: 1.55,
                          }}
                        >
                          {turn.message}
                        </Typography>
                      </Stack>
                    ))}
                    <div ref={transcriptEndRef} />
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.tokens.text.tertiary }}>
                    Nobody said anything on this call.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        ) : (
          /* ── Act one: the call is happening ───────────────────────── */
          <>
            <Box>
              <Typography sx={sectionLabelSx}>Progress</Typography>
              <Stack spacing={0} sx={{ mt: 1.25 }}>
                {stagesFor(phase).map((stage) => (
                  <Stack
                    key={stage.label}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ py: 0.9 }}
                  >
                    {stage.state === 'done' ? (
                      <CheckCircleRoundedIcon
                        sx={{ fontSize: 18, color: theme.tokens.semantic.success }}
                      />
                    ) : stage.state === 'active' ? (
                      <Box
                        sx={{
                          width: 18,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            bgcolor: theme.tokens.semantic.info,
                            animation: `${pulse} 1.4s ease-in-out infinite`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 18,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            border: `1.5px solid ${theme.tokens.border.strong}`,
                          }}
                        />
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          stage.state === 'pending'
                            ? theme.tokens.text.tertiary
                            : theme.tokens.text.primary,
                        fontWeight: stage.state === 'active' ? 600 : 400,
                      }}
                    >
                      {stage.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                borderRadius: `${theme.ds.radius.md}px`,
                border: `1px solid ${theme.tokens.border.subtle}`,
                bgcolor: theme.tokens.surface.sunken,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={sectionLabelSx}>Asking</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {call?.objective || '—'}
              </Typography>
            </Box>

            {/* The modal covers the chat, so without this the person watching
                the call cannot see the policy record — and when the carrier
                asks for something, has no way to tell whether the agent can
                answer it. This is the same sheet the agent is reading from. */}
            {call?.verificationFacts && (
              <Box>
                <Typography sx={sectionLabelSx}>
                  What the agent can verify with
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    m: 0,
                    maxHeight: 190,
                    overflowY: 'auto',
                    borderRadius: `${theme.ds.radius.md}px`,
                    border: `1px solid ${theme.tokens.border.subtle}`,
                    bgcolor: theme.tokens.surface.raised,
                    px: 2,
                    py: 1.5,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.76rem',
                    lineHeight: 1.7,
                    color: theme.tokens.text.secondary,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {call.verificationFacts}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={1.25} alignItems="center">
              <CircularProgress size={13} thickness={6} />
              <Typography variant="caption" sx={{ color: theme.tokens.text.tertiary }}>
                {phase === 'wrapping'
                  ? 'Reading back what the call established…'
                  : 'The transcript and the answer arrive together when the call ends.'}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </AppDialog>
  );
}
